import { useEffect, useRef } from "react";
import {
    useLazyGetClaimsSearchQuery,
    useLazyGetClaimsPaymentSearchQuery,
    useLazyGetLeavesSearchQuery,
    useLazyGetIntermittentLeavesSearchQuery,
} from "../../slices/dataviewApiSlice";
import { useLazyGetChangesReportQuery } from "../../slices/saveReportViewSlice";
import {
    getHighVolumeReport,
    getPresentDayFormat,
    getAdjustedEffectiveEndDateForApi,
} from "../../utils/helpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
    leaveType,
    disabilityType,
    paymentHistoryType,
    changesReportType,
    absenceType,
    lifeType,
    documentsType,
} from "../../constants";
import PropTypes from "prop-types";
import { useLazyGetODSLeaveChangesQuery } from "../../slices/OdsApiSlice";
import { useLazyGetDocumentsQuery } from "../../slices/documentsApiSlice";

// Documents search can return a retryable 503 timeout; try at most this many times.
const MAX_DOCUMENTS_ATTEMPTS = 3;

const isRetryableDocumentsError = (error) =>
    error?.status === 503 &&
    error?.data?.errorCode === "SEARCH_TIMEOUT_RETRYABLE";

const callWithDocumentsRetry = async (trigger) => {
    let attempt = 0;
    while (true) {
        try {
            return await trigger().unwrap();
        } catch (error) {
            attempt += 1;
            if (
                attempt >= MAX_DOCUMENTS_ATTEMPTS ||
                !isRetryableDocumentsError(error)
            ) {
                throw error;
            }
        }
    }
};

const ReportDataFetcher = (props) => {
    const {
        reportViewType,
        selectedIsfOrganizationIds,
        selectedCoverageType,
        selectedPeriodScheduleType,
        selectedPeriodStatus,
        selectedIncludeHistoricalLeave,
        selectedReportingPeriod,
        selectedPolicyDivision,
        selectedReportingGroup,
        subviewName,
        subviewMap,
        setReportTableData,
        onApplyFilter,
        setOnApplyFilter,
        getCommonDateFilters,
        setIsFetchingData,
        setIsError,
        setTotalResults,
        setRetrievedCount,
        setIsPaginating,
        filterStatusValues,
        filterAbsenceLeaveStatusValues,
        filterAbsencePaymentStatusValues,
        filterAbsenceClaimStatusValues,
        filteredPaymentsStatusValues,
        reportTableDisplayInformation,
        selectedLeaveScheduleType,
        setLoaderStatus,
        filterProtectionStatusValues,
        filterApplyingReportingTypeValues,
        dropdownSelectedReport,
        user,
        multiFactorReport,
        maxDisplayRows,
        setHighVolumeCardMessage,
    } = props;

    const { allPagingCounts, enableHighVolumeExport } = useFlags();
    const {
        changes: changePagingCount,
        claim: claimPagingCount,
        documents: documentPagingCount,
        leave: leavePagingCount,
        payments: paymentPagingCount,
    } = allPagingCounts || {};

    const {
        enableExternalUser,
        enableInternalUser,
        enableOrganizationList,
        isfUserIds,
        subViewNames,
    } = enableHighVolumeExport;

    const hasLoggedClientSideFilterRef = useRef(false);

    const isHighVolumeReportResult = getHighVolumeReport({
        enableExternalUser,
        enableInternalUser,
        enableOrganizationList,
        isfUserIds,
        subViewNames,
        user,
    });
    const getHighVolumeReportResult = (totalResults) => {
        if (isHighVolumeReportResult && dropdownSelectedReport) {
            const isSubViewEnabled = subViewNames?.includes(
                dropdownSelectedReport?.subViewName
            );
            if (!isSubViewEnabled) {
                return false;
            }
            const result = totalResults * multiFactorReport > maxDisplayRows;
            return result;
        }
        setHighVolumeCardMessage([]);
        return false;
    };

    // API hooks
    const [getLeavesSearch] = useLazyGetLeavesSearchQuery();
    const [getClaimsSearch] = useLazyGetClaimsSearchQuery();
    const [getClaimsPaymentSearch] = useLazyGetClaimsPaymentSearchQuery();
    const [getChangesReport] = useLazyGetChangesReportQuery();
    const [getODSLeaveChanges] = useLazyGetODSLeaveChangesQuery();
    const [getIntermittentLeavesSearch] =
        useLazyGetIntermittentLeavesSearchQuery();
    const [getDocuments] = useLazyGetDocumentsQuery();

    // Utility functions
    const getPageCount = (viewType) => {
        const pageCounts = {
            [disabilityType]: claimPagingCount,
            [leaveType]: leavePagingCount,
            [paymentHistoryType]: paymentPagingCount,
            [changesReportType]: changePagingCount,
            [documentsType]: documentPagingCount || 100,
        };
        return pageCounts[viewType] || 0;
    };

    const ensureKeysExist = (dataArray, requiredKeys) => {
        return dataArray.map((item) => {
            const newItem = { ...item };
            requiredKeys.forEach((requiredProp) => {
                if (
                    !newItem.hasOwnProperty(requiredProp) ||
                    newItem[requiredProp] === null ||
                    newItem[requiredProp] === undefined ||
                    newItem[requiredProp] === "" ||
                    (Array.isArray(newItem[requiredProp]) &&
                        newItem[requiredProp].length === 0) ||
                    (typeof newItem[requiredProp] === "object" &&
                        !Array.isArray(newItem[requiredProp]) &&
                        Object.keys(newItem[requiredProp]).length === 0)
                ) {
                    newItem[requiredProp] = "—";
                }
            });
            return newItem;
        });
    };

    const getReportingGroupFilter = (
        maxLength = 100,
        keyName = "reportingGroup"
    ) => {
        if (
            selectedReportingGroup.length > 0 &&
            selectedReportingGroup.length <= maxLength
        ) {
            return {
                [keyName]: selectedReportingGroup.map((item) => item.value),
            };
        }
        return {};
    };

    // Threshold limits above which the EDI API no longer applies server-side
    // filtering, so the application filters the response instead.
    const POLICY_DIVISION_THRESHOLD = 500;
    const REPORTING_GROUP_THRESHOLD = 100;
    const CHANGES_REPORTING_GROUP_THRESHOLD = 200;

    const needsClientDivisionFilter =
        selectedPolicyDivision.length > POLICY_DIVISION_THRESHOLD;
    const needsClientGroupFilter =
        reportViewType === changesReportType
            ? selectedReportingGroup.length > CHANGES_REPORTING_GROUP_THRESHOLD
            : selectedReportingGroup.length > REPORTING_GROUP_THRESHOLD;

    const selectedDivisionSet = new Set(
        (selectedPolicyDivision || []).map((v) => String(v).toLowerCase())
    );

    const selectedGroupKeySet = new Set(
        (selectedReportingGroup || [])
            .map((g) => String(g?.value ?? "").trim())
            .filter(Boolean)
    );

    const applyClientSideFilters = (
        rows = [],
        { enforceDivision = false, enforceGroup = false } = {}
    ) => {
        if (!Array.isArray(rows) || rows.length === 0) return [];
        if (!enforceDivision && !enforceGroup) return rows;
        if (enforceDivision && !needsClientDivisionFilter) return rows;
        if (enforceGroup && !needsClientGroupFilter) return rows;

        const filteredRows = rows.filter((row) => {
            if (enforceDivision && needsClientDivisionFilter) {
                const policyId = String(row?.groupAgreementId ?? "").trim();
                const divId = String(row?.divisionId ?? "").trim();

                const hasPolicyId =
                    policyId && policyId !== "—" && policyId !== "-";
                const hasDivId = divId && divId !== "—" && divId !== "-";

                if (hasPolicyId && hasDivId) {
                    const combined = `${policyId}-${divId}`.toLowerCase();
                    if (!selectedDivisionSet.has(combined)) {
                        return false;
                    }
                }
            }

            if (enforceGroup && needsClientGroupFilter) {
                const employerGroupNum = String(
                    row?.employerReportGroupNumber ?? ""
                ).trim();
                const hasEmployerGroupNum =
                    employerGroupNum &&
                    employerGroupNum !== "—" &&
                    employerGroupNum !== "-";

                if (
                    hasEmployerGroupNum &&
                    !selectedGroupKeySet.has(employerGroupNum)
                ) {
                    return false;
                }
            }

            return true;
        });

        if (
            filteredRows.length < rows.length &&
            !hasLoggedClientSideFilterRef.current
        ) {
            logClientSideFilteringApplied(reportViewType);
            hasLoggedClientSideFilterRef.current = true;
        }

        return filteredRows;
    };

    // State management utilities
    const initializeFetchState = () => {
        setReportTableData([]);
        setHighVolumeCardMessage([]);
        setIsFetchingData(true);
        setIsPaginating(false);
        setLoaderStatus?.(true);
        hasLoggedClientSideFilterRef.current = false;
    };

    const handleFetchError = (error) => {
        setIsError(true);
        setIsFetchingData(false);
        setIsPaginating(false);
        setReportTableData([]);
        setHighVolumeCardMessage([]);
        setLoaderStatus?.(false);
    };

    const updateFetchProgress = (newRecords, totalResults, isFirstCall) => {
        setRetrievedCount((prevCount) => prevCount + newRecords);

        if (isFirstCall) {
            setTotalResults(totalResults);
            setIsFetchingData(false);
            setIsError(false);
        }
    };

    const finalizeFetch = () => {
        setIsPaginating(false);
        setOnApplyFilter(false);
        setLoaderStatus?.(false);
    };

    const logClientSideFilteringApplied = (reportType) => {
        const usesDivisionFiltering = [
            disabilityType,
            paymentHistoryType,
            lifeType,
            absenceType,
        ].includes(reportType);
        const usesGroupFiltering = [
            leaveType,
            absenceType,
            changesReportType,
        ].includes(reportType);

        const reasons = [];
        if (usesDivisionFiltering && needsClientDivisionFilter) {
            reasons.push(
                `${selectedPolicyDivision.length} policy divisions (> ${POLICY_DIVISION_THRESHOLD})`
            );
        }
        if (usesGroupFiltering && needsClientGroupFilter) {
            const groupThreshold =
                reportType === changesReportType
                    ? CHANGES_REPORTING_GROUP_THRESHOLD
                    : REPORTING_GROUP_THRESHOLD;
            reasons.push(
                `${selectedReportingGroup.length} reporting groups (> ${groupThreshold})`
            );
        }
        if (reasons.length === 0) return;

        console.log(
            `[ClientSideFilter] Application-side filtering applied for "${reportType}" report because ${reasons.join(
                " and "
            )} were selected.`
        );
    };

    // API call builders
    const buildLeaveFilters = (
        startIndex,
        countPerPage,
        useIntermittent = false
    ) => {
        const dateFilters = getCommonDateFilters();

        return {
            organizationId: selectedIsfOrganizationIds,
            coverageType: ["Leave"],
            effectiveStartDate: dateFilters.effectiveStartDate,
            effectiveEndDate: getAdjustedEffectiveEndDateForApi({
                effectiveEndDate: dateFilters.effectiveEndDate,
                subviewName,
            }),
            leavePeriodStatus: selectedPeriodStatus.map((item) => item.value),
            leaveStatus: filterStatusValues,
            protectionStatus: filterProtectionStatusValues,
            historicalLeaveInd: selectedIncludeHistoricalLeave?.map(
                (obj) => obj.value
            ),
            leaveScheduleType: useIntermittent
                ? selectedLeaveScheduleType.map((item) => item.value)
                : selectedPeriodScheduleType.map((item) => item.value),
            ...getReportingGroupFilter(),
            startIndex: 0,
            startPage: startIndex / countPerPage + 1,
            count: countPerPage,
            effectiveDateNm: filterApplyingReportingTypeValues,
        };
    };

    const buildClaimFilters = (
        startIndex,
        countPerPage,
        excludeLeave = false
    ) => {
        const dateFilters = getCommonDateFilters("claims");

        return {
            organizationId: selectedIsfOrganizationIds,
            claimCoverageTypes: excludeLeave
                ? selectedCoverageType
                    .filter((obj) => obj.value !== "Leave")
                    .map((obj) => obj.value)
                : selectedCoverageType.map((obj) => obj.value),
            effectiveStartDate: dateFilters.effectiveStartDate,
            effectiveEndDate: getAdjustedEffectiveEndDateForApi({
                effectiveEndDate: dateFilters.effectiveEndDate,
                subviewName,
            }),
            claimStatus: filterStatusValues,
            groupAgreementDivision:
                selectedPolicyDivision.length > 500
                    ? ""
                    : selectedPolicyDivision,
            startIndex: 0,
            startPage: startIndex / countPerPage + 1,
            count: countPerPage,
            effectiveDateNm: filterApplyingReportingTypeValues,
        };
    };

    const buildPaymentFilters = (startIndex, countPerPage) => {
        const dateFilters = getCommonDateFilters();

        return {
            organizationId: selectedIsfOrganizationIds,
            claimCoverageTypes: selectedCoverageType.map((obj) => obj.value),
            effectiveStartDate: dateFilters.effectiveStartDate,
            effectiveEndDate: getAdjustedEffectiveEndDateForApi({
                effectiveEndDate: dateFilters.effectiveEndDate,
                subviewName,
            }),
            paymentStatus: filteredPaymentsStatusValues,
            groupAgreementDivision:
                selectedPolicyDivision.length > 500
                    ? ""
                    : selectedPolicyDivision,
            startIndex: 0,
            startPage: startIndex / countPerPage + 1,
            count: countPerPage,
        };
    };

    // Single report type fetchers
    const fetchSingleReportData = async () => {
        initializeFetchState();

        try {
            let startIndex = 0;
            const countPerPage = getPageCount(reportViewType);
            let hasMoreData = true;
            let isFirstCall = true;
            const requiredKeys = reportTableDisplayInformation.map(
                (col) => col.accessorKey
            );

            while (hasMoreData) {
                if (startIndex > 0) setIsPaginating(true);

                const response = await fetchSingleReportPage(
                    startIndex,
                    countPerPage
                );
                const { data, totalResults, recordCount } =
                    processSingleReportResponse(response, requiredKeys);

                updateFetchProgress(recordCount, totalResults, isFirstCall);
                if (isFirstCall) {
                    isFirstCall = false;
                    // Check if this is a high volume report and stop pagination
                    if (getHighVolumeReportResult(totalResults)) {
                        hasMoreData = false;
                    }
                }

                updateTableData(data, reportViewType);

                startIndex += countPerPage;
                hasMoreData = hasMoreData && startIndex < totalResults;
            }
        } catch (error) {
            handleFetchError(error);
        } finally {
            finalizeFetch();
        }
    };

    const fetchSingleReportPage = async (startIndex, countPerPage) => {
        const apiCalls = {
            [leaveType]: () => {
                if (subviewName === "Leave Changes") {
                    return getODSLeaveChanges({
                        startPage: startIndex / countPerPage + 1,
                        startIndex: startIndex,
                        count: countPerPage,
                        startDate: getPresentDayFormat(
                            selectedReportingPeriod?.value?.effectiveStartDt
                        ),
                        endDate: getPresentDayFormat(
                            selectedReportingPeriod?.value?.effectiveEndDt
                        ),
                        isfOrganizationIds: selectedIsfOrganizationIds,
                        ...getReportingGroupFilter(200, "reportingGroups"),
                    });
                }

                const filters = buildLeaveFilters(
                    startIndex,
                    countPerPage,
                    subviewMap === "IntermittentLeave" ||
                    subviewMap === "ReducedScheduleLeave" ||
                    subviewMap === "ContinuousLeave" ||
                    subviewMap === "SummaryContinuousLeave"
                );
                const useIntermittent =
                    subviewMap === "IntermittentLeave" ||
                    subviewMap === "ReducedScheduleLeave" ||
                    subviewMap === "ContinuousLeave" ||
                    subviewMap === "SummaryContinuousLeave";
                return useIntermittent
                    ? getIntermittentLeavesSearch({
                        filters,
                        formatType: subviewMap,
                    })
                    : getLeavesSearch({ filters, formatType: subviewMap });
            },
            [disabilityType]: () => {
                const filters = buildClaimFilters(startIndex, countPerPage);
                return getClaimsSearch({ filters });
            },
            [paymentHistoryType]: () => {
                const filters = buildPaymentFilters(startIndex, countPerPage);
                return getClaimsPaymentSearch({ filters });
            },
            [changesReportType]: () => {
                return getChangesReport({
                    startPage: startIndex / countPerPage + 1,
                    count: countPerPage,
                    startDate: getPresentDayFormat(
                        selectedReportingPeriod?.value?.effectiveStartDt
                    ),
                    isfOrganizationIds: selectedIsfOrganizationIds,
                    ...getReportingGroupFilter(200, "reportingGroups"),
                });
            },
            [documentsType]: () => {
                const dateFilters = getCommonDateFilters();
                // The same policy-division can be assigned under multiple selected orgs.
                const uniquePolicyDivisions = [
                    ...new Set(selectedPolicyDivision),
                ];
                return getDocuments({
                    filters: {
                        documentType: "TaxReportingDocuments",
                        isfOrganizationIds: selectedIsfOrganizationIds,
                        isfUserId: user?.userId,
                        effectiveStartDt: dateFilters.effectiveStartDate,
                        effectiveEndDt: dateFilters.effectiveEndDate,
                        policyAndDivisions:
                            uniquePolicyDivisions.length > 500
                                ? ""
                                : uniquePolicyDivisions,
                        startPage: 1,
                        count: 100,
                    },
                });
            },
        };

        if (reportViewType === documentsType) {
            return await callWithDocumentsRetry(apiCalls[reportViewType]);
        }

        return await apiCalls[reportViewType]().unwrap();
    };

    const processSingleReportResponse = (response, requiredKeys) => {
        const responseProcessors = {
            [leaveType]: (res) => {
                const data = applyClientSideFilters(
                    ensureKeysExist(res?.LeaveSearch || [], requiredKeys),
                    { enforceGroup: true }
                );
                return {
                    data,
                    totalResults: res?.searchResultMetadata?.totalResults || 0,
                    recordCount: data.length,
                };
            },
            [disabilityType]: (res) => {
                const data = applyClientSideFilters(
                    ensureKeysExist(res?.claimsSearch || [], requiredKeys),
                    { enforceDivision: true }
                );
                return {
                    data,
                    totalResults: res?.searchResultMetadata?.totalResults || 0,
                    recordCount: data.length,
                };
            },
            [paymentHistoryType]: (res) => {
                const data = applyClientSideFilters(
                    ensureKeysExist(
                        res?.claimsPaymentSearch || [],
                        requiredKeys
                    ),
                    { enforceDivision: true }
                );
                return {
                    data,
                    totalResults: res?.searchResultMetadata?.totalResults || 0,
                    recordCount: data.length,
                };
            },
            [changesReportType]: (res) => {
                const data = applyClientSideFilters(
                    ensureKeysExist(res?.data || [], requiredKeys),
                    { enforceGroup: true }
                );
                return {
                    data,
                    totalResults: res?.headers?.totalResults || 0,
                    recordCount: data.length,
                };
            },
            [documentsType]: (res) => {
                const data = ensureKeysExist(
                    res?.documents || [],
                    requiredKeys
                );
                return {
                    data,
                    totalResults: res?.searchResultMetadata?.totalResults || 0,
                    recordCount: data.length,
                };
            },
        };

        return responseProcessors[reportViewType](response);
    };

    // Multi-source report type fetchers
    const fetchMultiSourceReportData = async () => {
        initializeFetchState();

        try {
            const fetchers = {
                [absenceType]: fetchAbsenceData,
                [lifeType]: fetchLifeData,
            };

            await fetchers[reportViewType]();
        } catch (error) {
            handleFetchError(error);
        } finally {
            finalizeFetch();
        }
    };
    const fetchAbsenceData = async () => {
        let claimsStartIndex = 0;
        let leavesStartIndex = 0;
        const claimsPageSize = claimPagingCount;
        const leavesPageSize = leavePagingCount;
        let hasMoreClaims = true;
        let hasMoreLeaves = true;
        let isFirstCall = true;
        const requiredKeys = reportTableDisplayInformation.map(
            (col) => col.accessorKey
        );

        while (hasMoreClaims || hasMoreLeaves) {
            if (claimsStartIndex > 0 || leavesStartIndex > 0)
                setIsPaginating(true);

            const [claimsResponse, leavesResponse] = await Promise.all([
                hasMoreClaims
                    ? fetchAbsenceClaimsPage(claimsStartIndex, claimsPageSize)
                    : null,
                hasMoreLeaves
                    ? fetchAbsenceLeavesPage(leavesStartIndex, leavesPageSize)
                    : null,
            ]);

            const claimsData = ensureKeysExist(
                claimsResponse?.claimsSearch || [],
                requiredKeys
            );
            const leavesData = ensureKeysExist(
                leavesResponse?.LeaveSearch || [],
                requiredKeys
            );
            const filteredClaimsData = applyClientSideFilters(claimsData, {
                enforceDivision: true,
            });
            const filteredLeavesData = applyClientSideFilters(leavesData, {
                enforceGroup: true,
            });

            const totalClaimsResults =
                claimsResponse?.searchResultMetadata?.totalResults || 0;
            const totalLeavesResults =
                leavesResponse?.searchResultMetadata?.totalResults || 0;
            const newRecords =
                filteredClaimsData.length + filteredLeavesData.length;

            updateCombinedTableData(filteredClaimsData, filteredLeavesData);
            updateFetchProgress(
                newRecords,
                totalClaimsResults + totalLeavesResults,
                isFirstCall
            );

            if (isFirstCall) {
                isFirstCall = false;
                // Check if this is a high volume report and stop pagination
                if (
                    getHighVolumeReportResult(
                        totalClaimsResults + totalLeavesResults
                    )
                ) {
                    hasMoreClaims = false;
                    hasMoreLeaves = false;
                }
            }

            claimsStartIndex += claimsPageSize;
            leavesStartIndex += leavesPageSize;
            hasMoreClaims =
                hasMoreClaims && claimsStartIndex < totalClaimsResults;
            hasMoreLeaves =
                hasMoreLeaves && leavesStartIndex < totalLeavesResults;
        }
    };

    const fetchLifeData = async () => {
        // When subviewMap is "Payments", fetch only payments data
        if (subviewMap === "Payments") {
            let paymentsStartIndex = 0;
            const paymentsPageSize = paymentPagingCount;
            let hasMorePayments = true;
            let isFirstCall = true;
            const requiredKeys = reportTableDisplayInformation.map(
                (col) => col.accessorKey
            );

            while (hasMorePayments) {
                if (paymentsStartIndex > 0) setIsPaginating(true);

                const paymentsResponse = await fetchLifePaymentsPage(
                    paymentsStartIndex,
                    paymentsPageSize
                );
                const paymentsData = ensureKeysExist(
                    paymentsResponse?.claimsPaymentSearch || [],
                    requiredKeys
                );
                const filteredPaymentsData = applyClientSideFilters(
                    paymentsData,
                    { enforceDivision: true }
                );

                const totalPaymentsResults =
                    paymentsResponse?.searchResultMetadata?.totalResults || 0;
                const newRecords = filteredPaymentsData.length;

                setReportTableData((prevData) => [
                    ...prevData,
                    ...filteredPaymentsData.filter(
                        (item) =>
                            !prevData.some(
                                (existing) => existing.claimId === item.claimId
                            )
                    ),
                ]);
                updateFetchProgress(
                    newRecords,
                    totalPaymentsResults,
                    isFirstCall
                );

                if (isFirstCall) {
                    isFirstCall = false;
                    // Check if this is a high volume report and stop pagination
                    if (getHighVolumeReportResult(totalPaymentsResults)) {
                        hasMorePayments = false;
                    }
                }

                paymentsStartIndex += paymentsPageSize;
                hasMorePayments =
                    hasMorePayments &&
                    paymentsStartIndex < totalPaymentsResults;
            }
        } else if (subviewMap === "Claims") {
            let claimsStartIndex = 0;
            const claimsPageSize = claimPagingCount;
            let hasMoreClaims = true;
            let isFirstCall = true;
            const requiredKeys = reportTableDisplayInformation.map(
                (col) => col.accessorKey
            );

            while (hasMoreClaims) {
                if (claimsStartIndex > 0) setIsPaginating(true);
                const claimsResponse = await fetchLifeClaimsPage(
                    claimsStartIndex,
                    claimsPageSize
                );

                const claimsData = ensureKeysExist(
                    claimsResponse?.claimsSearch || [],
                    requiredKeys
                );
                const filteredClaimsData = applyClientSideFilters(claimsData, {
                    enforceDivision: true,
                });

                const totalClaimsResults =
                    claimsResponse?.searchResultMetadata?.totalResults || 0;
                const newRecords = filteredClaimsData.length;
                setReportTableData((prevData) => [
                    ...prevData,
                    ...filteredClaimsData.filter(
                        (item) =>
                            !prevData.some(
                                (existing) => existing.claimId === item.claimId
                            )
                    ),
                ]);

                updateFetchProgress(
                    newRecords,
                    totalClaimsResults,
                    isFirstCall
                );

                if (isFirstCall) {
                    isFirstCall = false;
                    // Check if this is a high volume report and stop pagination
                    if (getHighVolumeReportResult(totalClaimsResults)) {
                        hasMoreClaims = false;
                    }
                }

                claimsStartIndex += claimsPageSize;
                hasMoreClaims =
                    hasMoreClaims && claimsStartIndex < totalClaimsResults;
            }
        } else {
            // Default behavior: fetch both claims and payments
            let claimsStartIndex = 0;
            let paymentsStartIndex = 0;
            const claimsPageSize = claimPagingCount;
            const paymentsPageSize = paymentPagingCount;
            let hasMoreClaims = true;
            let hasMorePayments = true;
            let isFirstCall = true;
            const requiredKeys = reportTableDisplayInformation.map(
                (col) => col.accessorKey
            );

            while (hasMoreClaims || hasMorePayments) {
                if (claimsStartIndex > 0 || paymentsStartIndex > 0)
                    setIsPaginating(true);

                const [claimsResponse, paymentsResponse] = await Promise.all([
                    hasMoreClaims
                        ? fetchLifeClaimsPage(claimsStartIndex, claimsPageSize)
                        : null,
                    hasMorePayments
                        ? fetchLifePaymentsPage(
                            paymentsStartIndex,
                            paymentsPageSize
                        )
                        : null,
                ]);

                const claimsData = ensureKeysExist(
                    claimsResponse?.claimsSearch || [],
                    requiredKeys
                );
                const paymentsData = ensureKeysExist(
                    paymentsResponse?.claimsPaymentSearch || [],
                    requiredKeys
                );
                const filteredClaimsData = applyClientSideFilters(claimsData, {
                    enforceDivision: true,
                });
                const filteredPaymentsData = applyClientSideFilters(
                    paymentsData,
                    { enforceDivision: true }
                );

                const totalClaimsResults =
                    claimsResponse?.searchResultMetadata?.totalResults || 0;
                const totalPaymentsResults =
                    paymentsResponse?.searchResultMetadata?.totalResults || 0;
                const newRecords =
                    filteredClaimsData.length + filteredPaymentsData.length;

                updateCombinedTableData(
                    filteredClaimsData,
                    filteredPaymentsData
                );
                updateFetchProgress(
                    newRecords,
                    totalClaimsResults + totalPaymentsResults,
                    isFirstCall
                );

                if (isFirstCall) {
                    isFirstCall = false;
                    // Check if this is a high volume report and stop pagination
                    if (
                        getHighVolumeReportResult(
                            totalClaimsResults + totalPaymentsResults
                        )
                    ) {
                        hasMoreClaims = false;
                        hasMorePayments = false;
                    }
                }

                claimsStartIndex += claimsPageSize;
                paymentsStartIndex += paymentsPageSize;
                hasMoreClaims =
                    hasMoreClaims && claimsStartIndex < totalClaimsResults;
                hasMorePayments =
                    hasMorePayments &&
                    paymentsStartIndex < totalPaymentsResults;
            }
        }
    };

    // Specialized page fetchers

    const fetchAbsenceClaimsPage = async (startIndex, pageSize) => {
        const dateFilters = getCommonDateFilters("claims");
        const filters = {
            organizationId: selectedIsfOrganizationIds,
            claimCoverageTypes: selectedCoverageType
                .filter((obj) => obj.value !== "Leave")
                .map((obj) => obj.value),
            effectiveStartDate: dateFilters.effectiveStartDate,
            effectiveEndDate: getAdjustedEffectiveEndDateForApi({
                effectiveEndDate: dateFilters.effectiveEndDate,
                subviewName,
            }),
            claimStatus: filterAbsenceClaimStatusValues,
            groupAgreementDivision:
                selectedPolicyDivision.length > 500
                    ? ""
                    : selectedPolicyDivision,
            startIndex: 0,
            startPage: startIndex / pageSize + 1,
            count: pageSize,
            ...(filterApplyingReportingTypeValues.length > 0
                ? {
                    effectiveDateNm: [filterApplyingReportingTypeValues[1]],
                }
                : {}),
        };

        return await getClaimsSearch({ filters }).unwrap();
    };

    const fetchAbsenceLeavesPage = async (startIndex, pageSize) => {
        const dateFilters = getCommonDateFilters("leave");
        const filters = {
            organizationId: selectedIsfOrganizationIds,
            coverageType: ["Leave"],
            effectiveStartDate: dateFilters.effectiveStartDate,
            effectiveEndDate: getAdjustedEffectiveEndDateForApi({
                effectiveEndDate: dateFilters.effectiveEndDate,
                subviewName,
            }),
            leavePeriodStatus: selectedPeriodStatus.map((item) => item.value),
            leaveStatus: filterAbsenceLeaveStatusValues,
            protectionStatus: filterProtectionStatusValues,
            historicalLeaveInd: selectedIncludeHistoricalLeave?.map(
                (obj) => obj.value
            ),
            leaveScheduleType: selectedPeriodScheduleType.map(
                (item) => item.value
            ),
            ...getReportingGroupFilter(),
            startIndex: 0,
            startPage: startIndex / pageSize + 1,
            count: pageSize,
            ...(filterApplyingReportingTypeValues.length > 0
                ? {
                    effectiveDateNm: [filterApplyingReportingTypeValues[0]],
                }
                : {}),
        };

        return await getLeavesSearch({
            filters,
            formatType: subviewMap,
        }).unwrap();
    };

    const fetchLifeClaimsPage = async (startIndex, pageSize) => {
        const dateFilters = getCommonDateFilters();
        const filters = {
            organizationId: selectedIsfOrganizationIds,
            claimCoverageTypes: selectedCoverageType
                .filter((obj) => obj.value !== "Payment")
                .map((obj) => obj.value),
            effectiveStartDate: dateFilters.effectiveStartDate,
            effectiveEndDate: getAdjustedEffectiveEndDateForApi({
                effectiveEndDate: dateFilters.effectiveEndDate,
                subviewName,
            }),
            claimStatus:
                subviewMap === "Claims"
                    ? filterStatusValues
                    : filterAbsenceClaimStatusValues,
            groupAgreementDivision:
                selectedPolicyDivision.length > 500
                    ? ""
                    : selectedPolicyDivision,
            startIndex: 0,
            startPage: startIndex / pageSize + 1,
            count: pageSize,
        };
        return await getClaimsSearch({
            filters,
            formatType: subviewMap,
        }).unwrap();
    };

    const fetchLifePaymentsPage = async (startIndex, pageSize) => {
        const dateFilters = getCommonDateFilters();
        const filters = {
            organizationId: selectedIsfOrganizationIds,
            claimCoverageTypes: selectedCoverageType.map((obj) => obj.value),
            effectiveStartDate: dateFilters.effectiveStartDate,
            effectiveEndDate: getAdjustedEffectiveEndDateForApi({
                effectiveEndDate: dateFilters.effectiveEndDate,
                subviewName,
            }),
            paymentStatus:
                subviewMap === "Payments"
                    ? filteredPaymentsStatusValues
                    : [...new Set(filterAbsencePaymentStatusValues)],
            groupAgreementDivision:
                selectedPolicyDivision.length > 500
                    ? ""
                    : selectedPolicyDivision,
            startIndex: 0,
            startPage: startIndex / pageSize + 1,
            count: pageSize,
        };
        return await getClaimsPaymentSearch({
            filters,
            formatType: subviewMap,
        }).unwrap();
    };

    // Data update utilities
    const updateTableData = (newData, reportType) => {
        setReportTableData((prevData) => {
            const uniqueKeys = {
                [disabilityType]: "claimId",
                [leaveType]: "leaveId",
                [paymentHistoryType]: "claimId",
                [changesReportType]: null, // No deduplication needed
                [documentsType]: "documentId",
            };

            const uniqueKey = uniqueKeys[reportType];

            if (!uniqueKey) {
                return [...prevData, ...newData];
            }

            return [
                ...prevData,
                ...newData.filter(
                    (item) =>
                        !prevData.some(
                            (existing) =>
                                existing[uniqueKey] === item[uniqueKey]
                        )
                ),
            ];
        });
    };

    const updateCombinedTableData = (claimsData, otherData) => {
        setReportTableData((prevData) => [
            ...prevData,
            ...claimsData.filter(
                (item) =>
                    !prevData.some(
                        (existing) => existing.claimId === item.claimId
                    )
            ),
            ...otherData,
        ]);
    };

    // Main effect hooks
    useEffect(() => {
        if (!onApplyFilter) return;

        const singleSourceTypes = [
            leaveType,
            disabilityType,
            paymentHistoryType,
            changesReportType,
            documentsType,
        ];
        const multiSourceTypes = [absenceType, lifeType];

        if (singleSourceTypes.includes(reportViewType)) {
            fetchSingleReportData();
        } else if (multiSourceTypes.includes(reportViewType)) {
            fetchMultiSourceReportData();
        }
    }, [onApplyFilter]);

    return null;
};

ReportDataFetcher.propTypes = {
    reportViewType: PropTypes.string,
    selectedIsfOrganizationIds: PropTypes.arrayOf(PropTypes.string),
    selectedCoverageType: PropTypes.array,
    selectedPeriodScheduleType: PropTypes.array,
    selectedPeriodStatus: PropTypes.array,
    selectedIncludeHistoricalLeave: PropTypes.array,
    selectedReportingPeriod: PropTypes.object,
    subviewName: PropTypes.string,
    selectedPolicyDivision: PropTypes.array,
    selectedReportingGroup: PropTypes.array,
    subviewMap: PropTypes.string,
    getCommonDateFilters: PropTypes.func,
    setReportTableData: PropTypes.func,
    onApplyFilter: PropTypes.bool,
    setOnApplyFilter: PropTypes.func,
    setIsFetchingData: PropTypes.func,
    setIsError: PropTypes.func,
    setTotalResults: PropTypes.func,
    setRetrievedCount: PropTypes.func,
    setIsPaginating: PropTypes.func,
    filterStatusValues: PropTypes.array,
    filterAbsenceLeaveStatusValues: PropTypes.array,
    filterAbsencePaymentStatusValues: PropTypes.array,
    filterAbsenceClaimStatusValues: PropTypes.array,
    filteredPaymentsStatusValues: PropTypes.array,
    reportTableDisplayInformation: PropTypes.array,
    selectedLeaveScheduleType: PropTypes.array,
    setLoaderStatus: PropTypes.func,
    filterProtectionStatusValues: PropTypes.array,
    filterApplyingReportingTypeValues: PropTypes.array,
    dropdownSelectedReport: PropTypes.object,
    user: PropTypes.object,
    multiFactorReport: PropTypes.number,
    maxDisplayRows: PropTypes.number,
    setHighVolumeCardMessage: PropTypes.func,
};

export default ReportDataFetcher;
